import { FLAGGED_AREA_SAFETIES } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, Radio } from 'antd';
import { Eraser, PenLine } from 'lucide-react';
import type { RefObject } from 'react';
import { Skeleton } from '../../../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../../../shared/components/SkeletonRegion';
import { useFlaggedAreaEditor, type FlaggedAreaFormValues } from '../hooks/use-flagged-area-editor';
import { FLAGGED_AREA_SAFETY_HINTS, FLAGGED_AREA_SAFETY_LABELS } from '../policies/flagged-area-labels';

interface FlaggedAreaDrawerProps {
  /** An area id, or `new`. */
  areaId: string;
  onSaved: () => Promise<void>;
  onClose: () => void;
}

/** One area: its name, kind and note beside a map to draw its outline on. Stacked, map first, on a phone. */
export function FlaggedAreaDrawer({ areaId, onSaved, onClose }: FlaggedAreaDrawerProps) {
  const editor = useFlaggedAreaEditor({ areaId, onSaved, onClose });
  const { form, load, drawing, isMapConfigLoading, tileKey, containerRef } = editor;

  return (
    <Drawer
      open
      placement="right"
      size="min(1100px, 100vw)"
      title={areaId === 'new' ? 'New flagged area' : 'Flagged area'}
      onClose={onClose}
      aria-label="Flagged area"
    >
      {load.status === 'error' && <Alert type="error" showIcon title="Couldn't load this flagged area" />}
      {load.status === 'loading' && (
        <SkeletonRegion label="Loading flagged area">
          <Skeleton className="h-64 w-full" />
        </SkeletonRegion>
      )}
      {load.status === 'ready' && (
        <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[22rem_minmax(0,1fr)]">
          <Form<FlaggedAreaFormValues> form={form} layout="vertical" initialValues={{ safety: 'unsafe' }} onFinish={editor.save} className="order-2 lg:order-1">
            <Form.Item name="name" label="Name" rules={[{ required: true, whitespace: true, message: 'Enter a name' }]}>
              <Input maxLength={160} />
            </Form.Item>
            <Form.Item name="safety" label="Kind" extra={<SafetyHint form={form} />}>
              <Radio.Group options={FLAGGED_AREA_SAFETIES.map((value) => ({ value, label: FLAGGED_AREA_SAFETY_LABELS[value] }))} />
            </Form.Item>
            <Form.Item name="note" label="Note">
              <Input.TextArea maxLength={500} rows={3} />
            </Form.Item>
            {editor.problem && <Alert className="mb-4" type="error" showIcon title={editor.problem} />}
            <div className="flex gap-2">
              <Button type="primary" htmlType="submit" loading={editor.isSaving}>
                Save
              </Button>
              <Button onClick={onClose}>Cancel</Button>
            </div>
          </Form>
          <section aria-label="Flagged area map" className="order-1 flex flex-col gap-2 lg:order-2">
            <div className="flex items-center gap-2">
              <Button size="small" icon={<PenLine className="size-4" />} onClick={() => drawing.setDrawMode('polygon')}>
                Draw outline
              </Button>
              <Button size="small" icon={<Eraser className="size-4" />} disabled={!editor.polygon} onClick={editor.clearPolygon}>
                Clear outline
              </Button>
            </div>
            <AreaMap isLoading={isMapConfigLoading} tileKey={tileKey} mapRef={containerRef} />
          </section>
        </div>
      )}
    </Drawer>
  );
}

function SafetyHint({ form }: { form: ReturnType<typeof useFlaggedAreaEditor>['form'] }) {
  const safety = Form.useWatch('safety', form) ?? 'unsafe';
  return <span>{FLAGGED_AREA_SAFETY_HINTS[safety]}</span>;
}

interface AreaMapProps {
  isLoading: boolean;
  tileKey: string | null;
  mapRef: RefObject<HTMLDivElement | null>;
}

function AreaMap({ isLoading, tileKey, mapRef }: AreaMapProps) {
  if (isLoading) {
    return (
      <SkeletonRegion label="Loading map">
        <Skeleton className="h-[50dvh] w-full rounded-lg" />
      </SkeletonRegion>
    );
  }
  if (!tileKey) {
    return (
      <p className="rounded-lg border border-dashed border-line bg-page px-3 py-4 text-sm text-muted">
        No map on this deployment (no MapTiler key), so an area cannot be drawn.
      </p>
    );
  }
  return <div ref={mapRef} className="h-[50dvh] min-h-72 w-full rounded-lg border border-line" />;
}
